import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";

import AlertDialog from "../dialogs/alertDialog.js";
import BotpressTribalKnowledgeDialog from "../dialogs/botpressTribalKnowledgeDialog.js";
import BotpressTribalKnowledgeTable from "../ui/botpressTribalKnowledgeTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const parseMarkdownToRows = markdownFiles => {
  if (!Array.isArray(markdownFiles)) {
    return [];
  }

  return markdownFiles.flatMap(file => {
    const fileName =
      file?.name || file?.fileName || file?.filename || file?.path || file?.title || "Unknown File";

    const markdownContent =
      file?.content || file?.text || file?.markdown || file?.body || file?.value || "";

    const lowerFileName = fileName.toLowerCase();

    if (
      !lowerFileName.endsWith(".md") &&
      !lowerFileName.endsWith(".markdown") &&
      file?.contentType !== "text/markdown" &&
      file?.mimeType !== "text/markdown"
    ) {
      return [];
    }

    const lines = markdownContent.split("\n");
    const rows = [];

    let currentHeader = "General";

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        return;
      }

      if (trimmedLine.startsWith("#")) {
        currentHeader = trimmedLine.replace(/^#+\s*/, "").trim() || "General";
        return;
      }

      const cleanedValue = trimmedLine
        .replace(/^[-*]\s*/, "")
        .replace(/^\d+\.\s*/, "")
        .trim();

      if (!cleanedValue) {
        return;
      }

      rows.push({
        id: `${fileName}-${index}`,
        topic: currentHeader,
        clarification: cleanedValue,
        sourceFile: fileName,
        lineNumber: index + 1,
      });
    });

    return rows;
  });
};

const BotpressTribalKnowledge = ({
  markdownFiles = [],
  knowledgeBaseFiles = [],
  widgetSize = "medium",
  minHeight = "333px",
  onRefreshTable = null,
  onCreateEntry = null,
  onEditEntry = null,
  onDeleteEntry = null,
}) => {
  const filesToParse = useMemo(() => {
    if (Array.isArray(markdownFiles) && markdownFiles.length > 0) {
      return markdownFiles;
    }

    if (Array.isArray(knowledgeBaseFiles)) {
      return knowledgeBaseFiles;
    }

    return [];
  }, [knowledgeBaseFiles, markdownFiles]);

  const parsedRows = useMemo(() => {
    return parseMarkdownToRows(filesToParse);
  }, [filesToParse]);

  const [rows, setRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDeleteEntry, setSelectedDeleteEntry] = useState(null);

  useEffect(() => {
    setRows(parsedRows);
  }, [parsedRows]);

  const onCreateTribalKnowledgeEntry = () => {
    const newEntry = {
      id: null,
      topic: "",
      clarification: "",
      sourceFile: "Manual Entry",
      lineNumber: "",
    };

    if (onCreateEntry) {
      onCreateEntry(newEntry);
    }

    setSelectedEntry(newEntry);
  };

  const onEditTribalKnowledgeEntry = entry => {
    if (onEditEntry) {
      onEditEntry(entry);
    }

    setSelectedEntry(entry);
  };

  const onAcceptTribalKnowledgeEntry = entry => {
    const isNewEntry = selectedEntry?.id == null;

    const savedEntry = {
      ...entry,
      id: isNewEntry ? `manual-entry-${Date.now()}` : entry.id,
      sourceFile: entry.sourceFile || "Manual Entry",
    };

    setRows(previousRows => {
      if (isNewEntry) {
        return [savedEntry, ...previousRows];
      }

      return previousRows.map(row => (row.id === savedEntry.id ? savedEntry : row));
    });

    setSelectedEntry(null);
  };

  const onDeleteTribalKnowledgeEntry = entry => {
    setSelectedEntry(null);
    setSelectedDeleteEntry(entry);
  };

  const onAcceptDeleteEntry = () => {
    if (onDeleteEntry) {
      onDeleteEntry(selectedDeleteEntry);
    }

    setRows(previousRows => previousRows.filter(row => row.id !== selectedDeleteEntry.id));
    setSelectedDeleteEntry(null);
  };

  const onRefreshTribalKnowledge = () => {
    if (onRefreshTable) {
      onRefreshTable();
      return;
    }

    setRows(parsedRows);
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Tribal Knowledge"}
        text={rows != null ? `${rows.length} Entries` : "Entries"}
      >
        <OmniResponsiveButton
          icon={<Add />}
          tooltipTitle="Create a new tribal knowledge entry"
          buttonText="Entry"
          onClick={onCreateTribalKnowledgeEntry}
        />
      </WidgetTitle>

      <Box>
        <WidgetSurveyContent>
          <BotpressTribalKnowledgeTable
            data={rows}
            onRefreshTable={onRefreshTribalKnowledge}
            onEditEntry={onEditTribalKnowledgeEntry}
            onDeleteEntry={onDeleteTribalKnowledgeEntry}
          />
        </WidgetSurveyContent>
      </Box>

      {selectedEntry != null && (
        <BotpressTribalKnowledgeDialog
          open={selectedEntry != null}
          entryRecord={selectedEntry}
          dialogTitle={selectedEntry.id == null ? "Create Tribal Knowledge Entry" : "Edit Entry"}
          okMessage={selectedEntry.id == null ? "Create" : "Ok"}
          onAccept={onAcceptTribalKnowledgeEntry}
          onDelete={onDeleteTribalKnowledgeEntry}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedDeleteEntry != null && (
        <AlertDialog
          open={selectedDeleteEntry != null}
          dialogTitle={"Confirm"}
          dialogMessage={"Are you sure you want to delete this Tribal Knowledge entry?"}
          onAccept={onAcceptDeleteEntry}
          onClose={() => {
            setSelectedDeleteEntry(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  knowledgeBaseFiles:
    state.botpressSession.knowledgeBaseFiles ||
    state.botpressSession.knowledgeBaseDocuments ||
    state.botpressSession.knowledgeBaseMarkdownFiles ||
    state.botpressSession.knowledgeBaseSourceFiles ||
    [],
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressTribalKnowledge);
